import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import "./utils/firebase";
import "./index.css";
import { AnimatePresence } from "framer-motion";

import { registerSW } from "virtual:pwa-register";
import * as Sentry from "@sentry/react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { SnackbarProvider } from "notistack";
import { routes as dashboardRoutes } from "./dashboard/routes";
import { ErrorBoundary } from "./player/components/ErrorBoundary";
import { routes as playerRoutes } from "./player/routes";
import { auth, subscribeAppUpdate } from "./utils/firebase";
import { migrateData, seed } from "./utils/seedLocal";
import { getVideoFromDb } from "./utils/videoCache";

export interface Video {
	originalUrl: string;
	url?: string;
	id?: string;
	title?: string;
}

Sentry.init({
	dsn: "https://a02e8cbbe6e7e3ce40d54bdfaed017b7@o4504192995164160.ingest.sentry.io/4506361614893056",
	integrations: [
		new Sentry.BrowserTracing({
			// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
			// https://condorflix-dev.web.app/
			tracePropagationTargets: [
				"condorflix-dev.web.app",
				/.*\.firebaseapp\.com/,
				/https:\/\/condorflix-dev\.web\.app\/.*/,
				"condor.tvtreinamento.com.br",
			],
		}),
		new Sentry.Replay(),
	],
	// Performance Monitoring
	tracesSampleRate: 1.0, //  Capture 100% of the transactions
	// Session Replay
	replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
	replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const router = createBrowserRouter([playerRoutes, dashboardRoutes]);

if (
	window.api?.isInstalledPlayer ||
	window.location.href.includes("force-install-sw")
) {
	registerSW({
		immediate: true,
		// Kiosks never navigate, so the service worker would never look for a
		// new version on its own; poll every 20 minutes to pick up deploys
		onRegisteredSW(_swUrl, registration) {
			if (registration) {
				setInterval(
					() => {
						registration.update();
					},
					20 * 60 * 1000,
				);
			}
		},
	});

	// Forced update channel: writing meta/appUpdate makes every kiosk look for a
	// new build immediately, instead of waiting for the 20 minute poll.
	subscribeAppUpdate(() => {
		navigator.serviceWorker
			?.getRegistration()
			.then((registration) => registration?.update())
			.catch(() => undefined);
	});
}

ReactDOM.createRoot(
	document.getElementById("root")! as unknown as DocumentFragment,
).render(
	<React.StrictMode>
		<ErrorBoundary>
			<SnackbarProvider>
				<AnimatePresence mode="wait">
					<RouterProvider router={router} />
				</AnimatePresence>
			</SnackbarProvider>
		</ErrorBoundary>
	</React.StrictMode>,
);

if (import.meta.env.DEV) {
	window.getVideoFromDb = getVideoFromDb;

	window.loginWithEmailAndPassword = async (
		email: string,
		password: string,
	) => {
		return signInWithEmailAndPassword(auth, email, password);
	};

	// @ts-ignore
	window.seed = seed;
	// @ts-ignore
	window.migrateData = migrateData;
	// @ts-ignore
	window.enableDebug = () => {
		localStorage.setItem("debug", "true");
		window.location.reload();
	};
}
